import { useNavigate, useParams } from 'react-router-dom';
import useRule from '../../../hooks/useRule';
import React, { useMemo, useState } from 'react';
import { Config, SchemaItem } from '@hud/rules-dtos/types';
import { GetAllServicesResponse } from '@hud/portal-dtos/webapp-service.dto';
import { createColumnHelper, FilterFn, Row, SortingState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DetectionParametersGroup } from '@hud/web-components/Rules/DetectionParametersGroup';
import {
  ParameterDescriptor,
  ParameterType,
  SupportedDescriptors,
} from '@hud/web-components/Rules/types';
import RunningStatus from '@hud/web-components/RunningStatus/RunningStatus';
import { formatRelativeDateTime } from '@hud/web-components/utils';
import { EditRuleDrawer } from './EditRuleDrawer';
import { useUpdateAccountConfigMutation } from '../../../hooks/useUpdateAccountConfigMutation';
import {
  AboutContainer,
  AboutHeader,
  AboutTitle,
  AboutDescription,
  AboutButton,
  AboutContent,
  DetectionLogicContainer,
  DetectionLogicHeader,
  DetectionLogicTitle,
  DetectionLogicActions,
  DetectionLogicDescriptionContainer,
  DetectionLogicDescription,
  DetectionLogicParametersContainer,
  DetectionLogicParametersTitle,
  Header,
  HeaderContent,
  HeaderTitle,
  Main,
  EditButton,
  TableHeaderContainer,
  LastApplied,
  ViewIssuesButton,
  ServicesListRow,
  MoreItemButton,
  MoreItemsContainer,
  ServicesPopover,
  Service,
  ShownServices,
} from './HeadsUpRulePage.styles';
import { DefaultTable } from '@hud/web-components/Table/Table';
import useAllServices from '../../../hooks/useAllServices';
import { ServiceConfig } from '@hud/rules-dtos/api-rules.dtos';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Page } from '@hud/web-components/Layout/Page';
import { Button } from '@hud/web-components/Button/Button';
import { Search } from '@hud/web-components/Search/Search';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import useAddServiceConfigMutation from '../../../hooks/useAddServiceConfigMutation';
import useUpdateServiceConfigMutation from '../../../hooks/useUpdateServiceConfigMutation';
import useDeleteServiceConfigMutation from '../../../hooks/useDeleteServiceConfigMutation';
import { getIcon, getUnitString, ConfigParamType } from '../HeadsUpCommon';
import { useGlobalQueryString } from '../../../hooks/useGlobalFilter';
import { Popover } from '@hud/web-components/Popover/Popover';
import useAnalytics from '../../../hooks/useAnalytics';

export default function HeadsUpRulePage() {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const globalFilters = useGlobalQueryString();
  const { ruleId } = useParams<{ ruleId: string }>();
  const [serviceConfigToEdit, setServiceConfigToEdit] = useState<ServiceConfig | null>(
    null,
  );
  const ruleQuery = useRule(ruleId!);
  const allServicesQuery = useAllServices();
  const updateAccountConfigMutation = useUpdateAccountConfigMutation(ruleId!);
  const addServiceConfigMutation = useAddServiceConfigMutation(ruleId!);
  const updateServiceConfigMutation = useUpdateServiceConfigMutation(
    ruleId!,
    serviceConfigToEdit?.id ?? null,
  );
  const deleteServiceConfigMutation = useDeleteServiceConfigMutation(
    ruleId!,
    serviceConfigToEdit?.id ?? null,
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isAddServiceConfigDrawerOpen, setIsAddServiceConfigDrawerOpen] = useState(false);

  if (ruleQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (ruleQuery.error) {
    const error = ruleQuery.error as Error;
    return <div>Error: {error.message}</div>;
  }

  if (!ruleQuery.data) {
    return <div>Rule not found</div>;
  }

  const defaultBreadcrumbs = [{ title: 'Rules', to: '/heads-up/rules', nowrap: true }];

  const baseBreadcrumbs = defaultBreadcrumbs;

  const { rule, accountConfig, serviceConfigs } = ruleQuery.data;

  const configDescription = rule.configDescriptionTemplate.replace(
    /\{(\w+)(?:,\s*(.+?))?\}/g,
    (_, key: string, suffix: string): string => {
      const value =
        accountConfig?.config?.[key] ??
        rule.schema.find((item: SchemaItem) => item.name === key)?.defaultValue;

      return `<Value>${value?.toString() ?? key}${suffix ? ` ${suffix}` : ''}</Value>`;
    },
  );

  const descriptors = rule.schema.reduce<SupportedDescriptors>((acc, item) => {
    acc[item.name] = {
      type: ParameterType.Number,
      label: item.title,
      tooltip: item.description,
      recommendation: item.recommendation,
      icon: getIcon(item.type as ConfigParamType),
      unit: getUnitString(item.type as ConfigParamType),
      presetValues: [],
    } satisfies ParameterDescriptor;
    return acc;
  }, {});

  const handleSaveConfig = async (
    enabled: boolean,
    updatedConfig: Record<string, unknown>,
  ) => {
    await updateAccountConfigMutation.mutateAsync({
      enabled,
      config: updatedConfig as Record<string, string | number | number[] | null>,
    });
    analytics.capture('rule_edit_config_saved', {
      rule_id: rule.id,
      rule_edit_config_mode: 'detection',
      rule_config: { enabled: enabled, config: updatedConfig },
      service_ids: [],
    });
  };

  const handleDeleteConfig = async () => {
    await deleteServiceConfigMutation.mutateAsync();
    analytics.capture('rule_edit_config_deleted', {
      rule_id: rule.id,
      rule_edit_config_mode: 'service',
      service_ids: serviceConfigToEdit?.serviceIds ?? [],
      rule_config_id: Number(serviceConfigToEdit?.id ?? 0),
    });
  };

  const handleAddServiceConfig = async (
    enabled: boolean,
    config: Record<string, string | number | number[] | boolean | null>,
    serviceIds?: number[],
  ) => {
    if (!serviceIds) return;

    if (serviceConfigToEdit) {
      await updateServiceConfigMutation.mutateAsync({
        serviceIds,
        config,
        enabled,
      });
      setServiceConfigToEdit(null);
    } else {
      await addServiceConfigMutation.mutateAsync({
        serviceIds,
        config,
        enabled,
      });
    }
    analytics.capture('rule_edit_config_saved', {
      rule_id: rule.id,
      rule_edit_config_mode: 'service',
      service_ids: serviceIds,
      rule_config: { enabled: enabled, config },
    });
  };

  const handleOpenAddServiceConfigDrawer = (serviceConfig?: ServiceConfig) => {
    setServiceConfigToEdit(serviceConfig ?? null);
    setIsAddServiceConfigDrawerOpen(true);
    analytics.capture('rule_edit_config_clicked', {
      rule_id: rule.id,
      rule_edit_config_mode: 'service',
    });
  };

  const handleCloseAddServiceConfigDrawer = () => {
    setIsAddServiceConfigDrawerOpen(false);
  };

  const handleOpenEditDrawer = () => {
    setIsEditDrawerOpen(true);
    analytics.capture('rule_edit_config_clicked', {
      rule_id: rule.id,
      rule_edit_config_mode: 'detection',
    });
  };

  const handleCloseEditDrawer = () => {
    setIsEditDrawerOpen(false);
  };

  const effectiveInitialConfig: Config = {
    ...rule.schema.reduce((acc, item) => {
      acc[item.name] = item.defaultValue;
      return acc;
    }, {} as Config),
    ...accountConfig?.config,
  };

  return (
    <Page
      header={
        ruleQuery.isLoading ? 'loading' : [...baseBreadcrumbs, { title: rule.name }]
      }
      aria-label={`${rule.name} rule page`}
      aria-busy={ruleQuery.isLoading || updateAccountConfigMutation.isPending}
    >
      <Header>
        <HudIcon icon="other.ruleBadge" size={'55px'} />
        <HeaderContent>
          <HeaderTitle>{rule.name}</HeaderTitle>
          <ViewIssuesButton
            onClick={() =>
              navigate(`/heads-up/issues/?${globalFilters}&rules=${rule.id}`)
            }
          >
            <HudIcon icon="other.flame" size="20px" />
            View Issues
          </ViewIssuesButton>
        </HeaderContent>
      </Header>

      <Main>
        <DetectionLogicContainer
          variant="filled"
          role="region"
          aria-label="Detection logic overview"
        >
          <DetectionLogicHeader>
            <DetectionLogicTitle>
              <HudIcon icon="other.plug" size="24px" />
              Detection logic
            </DetectionLogicTitle>
            <DetectionLogicActions>
              <EditButton
                aria-label="Enabled"
                variant="outlined"
                onClick={handleOpenEditDrawer}
              >
                <RunningStatus status={rule.enabled ? 'enabled' : 'disabled'} />
              </EditButton>
              <EditButton variant="outlined" onClick={handleOpenEditDrawer}>
                <HudIcon icon="other.filters" size="16px" />
                Edit
              </EditButton>
            </DetectionLogicActions>
          </DetectionLogicHeader>
          <DetectionLogicDescriptionContainer
            role="region"
            aria-label="config description"
          >
            <DetectionLogicDescription>{configDescription}</DetectionLogicDescription>
          </DetectionLogicDescriptionContainer>
          {rule.schema.length > 0 && (
            <DetectionLogicParametersContainer>
              <DetectionLogicParametersTitle>Detect when</DetectionLogicParametersTitle>
              <DetectionParametersGroup
                key={JSON.stringify(ruleQuery.data)}
                detectionParams={rule.schema.map((item) => {
                  return {
                    id: item.name,
                    value:
                      (accountConfig?.config?.[item.name] as number) ??
                      (item.defaultValue as number),
                    operator: 'gte',
                  };
                })}
                descriptors={descriptors}
                readonly
              />
            </DetectionLogicParametersContainer>
          )}
          <LastApplied>
            <div>
              This configuration is applied for all services in the account by default
            </div>
            {accountConfig?.lastChangedAt && (
              <div>
                Last updated {formatRelativeDateTime(accountConfig.lastChangedAt)}
              </div>
            )}
          </LastApplied>
        </DetectionLogicContainer>

        <AboutContainer
          role="region"
          aria-label="About this rule"
          paramsLength={rule.schema.filter((param) => param.visible).length}
        >
          <AboutHeader>
            <HudIcon icon="other.info" size={'18px'} />
            <AboutTitle>About this rule</AboutTitle>
          </AboutHeader>

          <AboutContent role="region" aria-label="rule description">
            <AboutDescription>
              {isExpanded ? rule.fullDescription : rule.shortDescription}
            </AboutDescription>
            <AboutButton
              onClick={() => {
                setIsExpanded(!isExpanded);
                analytics.capture('rule_description_expanded', {
                  rule_id: rule.id,
                  rule_description_expanded: !isExpanded,
                });
              }}
            >
              {isExpanded ? 'Read less' : 'Read more'}
            </AboutButton>
          </AboutContent>
        </AboutContainer>
      </Main>

      <ServiceOverridesTable
        onAddServiceClicked={handleOpenAddServiceConfigDrawer}
        services={allServicesQuery.data?.services ?? []}
        serviceOverrides={serviceConfigs}
        loading={allServicesQuery.isLoading}
        schema={rule.schema}
      />

      <EditRuleDrawer
        opened={isEditDrawerOpen}
        onClose={handleCloseEditDrawer}
        initialEnabled={rule.enabled}
        schema={rule.schema}
        initialConfig={effectiveInitialConfig || {}}
        descriptors={descriptors}
        mode="detection"
        onSave={handleSaveConfig}
        ruleId={rule.id}
      />

      <EditRuleDrawer
        opened={isAddServiceConfigDrawerOpen}
        onClose={handleCloseAddServiceConfigDrawer}
        services={allServicesQuery.data?.services ?? []}
        initialEnabled={serviceConfigToEdit?.enabled ?? rule.enabled}
        schema={rule.schema}
        alreadyConfiguredServices={[
          ...new Set(serviceConfigs.map((config) => config.serviceIds).flat()),
        ]}
        initialConfig={
          serviceConfigToEdit
            ? {
                ...accountConfig?.config,
                ...serviceConfigToEdit.config,
              }
            : accountConfig?.config || {}
        }
        initialServiceIds={serviceConfigToEdit?.serviceIds ?? []}
        descriptors={descriptors}
        mode="service"
        onSave={handleAddServiceConfig}
        onDelete={handleDeleteConfig}
        ruleId={rule.id}
      />
    </Page>
  );
}

export type ServiceOverridesTableProps = {
  onAddServiceClicked: (serviceConfig?: ServiceConfig) => void;
  services: GetAllServicesResponse['services'];
  serviceOverrides: ServiceConfig[];
  loading: boolean;
  schema: SchemaItem[];
};

const ServiceOverridesTable = ({
  services,
  serviceOverrides,
  loading,
  onAddServiceClicked,
  schema,
}: ServiceOverridesTableProps) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const serviceIdToName = useMemo(() => {
    return new Map(services.map((service) => [service.id, service.name]));
  }, [services]);

  const columnHelper = createColumnHelper<ServiceConfig>();

  const columns = [
    columnHelper.accessor('serviceIds', {
      header: 'Services',
      cell: ({ row }) => {
        return (
          <ServicesList
            serviceIds={row.original.serviceIds}
            serviceIdToName={serviceIdToName}
          />
        );
      },
      maxSize: 400,
      minSize: 250,
    }),
    columnHelper.accessor('config', {
      header: 'Specific Configuration',
      cell: (cell) => {
        const value = cell.getValue();
        // single string like: Minimum degradation percentage (150%), Minimum invocation (250)
        return Object.entries(value)
          .map(([key, value]) => {
            const schemaItem = schema.find((item) => item.name === key);
            if (!schemaItem) {
              return `${key} (${value})`;
            }
            return `${schemaItem.title} (${value})`;
          })
          .join(', ');
      },
      maxSize: 500,
      minSize: 300,
    }),
    columnHelper.accessor('enabled', {
      header: 'Status',
      minSize: 120,
      maxSize: 120,
      cell: ({ row }) => {
        return <RunningStatus status={row.original.enabled ? 'enabled' : 'disabled'} />;
      },
      enableResizing: false,
    }),
    columnHelper.accessor('lastChangedAt', {
      header: 'Last Changed',
      maxSize: 150,
      cell: ({ row }) => {
        if (row.original.lastChangedAt > new Date(Date.now() - 60 * 1000)) {
          return 'Now';
        }
        return row.original.lastChangedAt
          ? formatRelativeDateTime(row.original.lastChangedAt)
          : 'N/A';
      },
      enableResizing: false,
    }),
    columnHelper.display({
      header: 'Actions',
      cell: ({ row }) => {
        return (
          <Button
            variant="text"
            onClick={() => {
              onAddServiceClicked(row.original);
            }}
          >
            Edit
          </Button>
        );
      },
      enableResizing: false,
    }),
  ];

  const customGlobalFilterFunction: FilterFn<ServiceConfig[][0]> = (
    row: Row<ServiceConfig[][0]>,
    _columnId: string,
    searchTerms: string,
  ) => {
    const serviceName = row.original.serviceIds
      .map((id) => services.find((service) => service.id === id)?.name)
      .join(' ')
      .toLowerCase();

    const configurationKeys = Object.keys(row.original.config)
      .map((key) => {
        const schemaItem = schema.find((item) => item.name === key);
        if (!schemaItem) {
          return key.toLowerCase();
        }
        return schemaItem.title.toLowerCase();
      })
      .join(' ')
      .toLowerCase();

    return (
      serviceName.includes(searchTerms.toLowerCase()) ||
      configurationKeys.includes(searchTerms.toLowerCase())
    );
  };

  const table = useReactTable({
    data: serviceOverrides ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,

    columnResizeMode: 'onChange',
    enableSortingRemoval: false,

    getColumnCanGlobalFilter: (_column) => {
      return true;
    },

    state: {
      pagination,
      sorting,
      globalFilter,
    },

    globalFilterFn: customGlobalFilterFunction,
  });

  return (
    <TableContainer.Container
      title={'Custom Configuration For Specific Services'}
      icon="entities.servicesBlank"
    >
      <TableHeaderContainer>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
        <Button
          variant="outlined"
          onClick={() => onAddServiceClicked()}
          aria-label="Add service custom configuration"
        >
          <HudIcon icon="menu.plus" size="12px" />
          Add
        </Button>
      </TableHeaderContainer>

      <DefaultTable
        table={table}
        label="Service specific configurations"
        loading={loading}
        loadingRows={5}
        aria-busy={loading}
        options={{
          emptyStateProps: {
            icon: 'entities.servicesBlank',
            expanded: true,
          },
        }}
      />
    </TableContainer.Container>
  );
};

export function ServicesList({
  serviceIds,
  serviceIdToName,
}: {
  serviceIds: number[];
  serviceIdToName: Map<number, string>;
}) {
  const [shownServices, moreServices] = [serviceIds.slice(0, 3), serviceIds.slice(3)];

  return (
    <ServicesListRow>
      <ShownServices>
        {shownServices.map((serviceId) => {
          return (
            <Service key={serviceId}>
              <HudIcon icon="entities.servicesBlank" size="16px" />
              {serviceIdToName.get(serviceId)}
            </Service>
          );
        })}
      </ShownServices>

      {moreServices && moreServices.length > 0 && (
        <MoreItemsContainer>
          <Popover
            position="top"
            body={
              <ServicesPopover>
                {moreServices.map((serviceId) => {
                  return (
                    <Service key={serviceId}>
                      <HudIcon icon="entities.servicesBlank" size="16px" />
                      {serviceIdToName.get(serviceId)}
                    </Service>
                  );
                })}
              </ServicesPopover>
            }
          >
            <MoreItemButton>+{moreServices.length}</MoreItemButton>
          </Popover>
        </MoreItemsContainer>
      )}
    </ServicesListRow>
  );
}
